import { useState, useEffect, useCallback } from 'react';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Bot, Search, Pen, BarChart, Code, Calendar, 
  ChevronRight, Folder, FileText
} from 'lucide-react';
import AgentCard from '../components/dashboard/AgentCard';
import TaskBrowser from '../components/dashboard/TaskBrowser';
import PromptEditor from '../components/dashboard/PromptEditor';
import { getAgents, getCategories, getTasks, getPrompts } from '../lib/api';

const iconMap = {
  Search: Search,
  Pen: Pen,
  BarChart: BarChart,
  Code: Code,
  Calendar: Calendar,
  Bot: Bot,
};

export default function Dashboard() {
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [prompts, setPrompts] = useState([]);
  
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  
  const [loading, setLoading] = useState(true);

  const loadAgents = useCallback(async () => {
    try {
      const res = await getAgents();
      setAgents(res.data);
      if (res.data.length > 0 && !selectedAgent) {
        setSelectedAgent(res.data[0]);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  }, [selectedAgent]);

  const loadCategories = useCallback(async (agentId) => {
    if (!agentId) return;
    try {
      const res = await getCategories(agentId);
      setCategories(res.data);
      if (res.data.length > 0) {
        setSelectedCategory(res.data[0]);
      } else {
        setSelectedCategory(null);
      }
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  const loadTasks = useCallback(async (agentId, categoryId) => {
    if (!agentId) return;
    try {
      const params = { agent_id: agentId };
      if (categoryId) params.category_id = categoryId;
      const res = await getTasks(params);
      setTasks(res.data);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  }, []);

  const loadPrompts = useCallback(async (taskId) => {
    if (!taskId) return;
    try {
      const res = await getPrompts({ task_id: taskId });
      setPrompts(res.data);
      if (res.data.length > 0) {
        setSelectedPrompt(res.data[0]);
      } else {
        setSelectedPrompt(null);
      }
    } catch (err) {
      console.error('Failed to load prompts:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadAgents();
      setLoading(false);
    };
    init();
  }, [loadAgents]);

  // Load categories when agent changes
  useEffect(() => {
    if (selectedAgent) {
      loadCategories(selectedAgent.id);
      setSelectedTask(null);
      setSelectedPrompt(null);
    }
  }, [selectedAgent, loadCategories]);

  // Load tasks when category changes
  useEffect(() => {
    if (selectedAgent) {
      loadTasks(selectedAgent.id, selectedCategory?.id);
    }
  }, [selectedAgent, selectedCategory, loadTasks]);

  // Load prompts when task changes
  useEffect(() => {
    if (selectedTask) {
      loadPrompts(selectedTask.id);
    } else {
      setPrompts([]);
      setSelectedPrompt(null);
    }
  }, [selectedTask, loadPrompts]);

  const handlePromptUpdate = (updatedPrompt) => {
    setSelectedPrompt(updatedPrompt);
    setPrompts(prev => prev.map(p => p.id === updatedPrompt.id ? updatedPrompt : p));
  };

  // Handle Langfuse import completion - refresh all data
  const handleImportComplete = useCallback(async () => {
    await loadAgents();
    // Reset selections to show new data
    setSelectedAgent(null);
    setSelectedCategory(null);
    setSelectedTask(null);
    setSelectedPrompt(null);
    setCategories([]);
    setTasks([]);
    setPrompts([]);
  }, [loadAgents]);

  return (
    <div className="h-full flex flex-col bg-[#030712] moon-glow" data-testid="dashboard">
      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <ResizablePanelGroup direction="horizontal">
          {/* Left Panel - Agents */}
          <ResizablePanel defaultSize={20} minSize={15} maxSize={30}>
            <div className="h-full bg-[#0F172A] border-r border-[#1E293B]">
              <div className="p-3 border-b border-[#1E293B]">
                <h2 className="text-xs font-medium text-[#64748B] uppercase tracking-wider">Agents</h2>
              </div>
              <ScrollArea className="h-[calc(100%-44px)]">
                <div className="p-2 space-y-1">
                  {agents.length === 0 && !loading ? (
                    <div className="p-4 text-center text-[#64748B] text-sm">
                      No agents yet. Create one to get started.
                    </div>
                  ) : (
                    agents.map(agent => (
                      <AgentCard
                        key={agent.id}
                        agent={agent}
                        icon={iconMap[agent.icon] || Bot}
                        selected={selectedAgent?.id === agent.id}
                        onClick={() => setSelectedAgent(agent)}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </ResizablePanel>

          <ResizableHandle />

          {/* Middle Panel - Tasks */}
          <ResizablePanel defaultSize={30} minSize={20} maxSize={40}>
            <TaskBrowser
              categories={categories}
              tasks={tasks}
              selectedCategory={selectedCategory}
              selectedTask={selectedTask}
              onSelectCategory={setSelectedCategory}
              onSelectTask={setSelectedTask}
              agentName={selectedAgent?.name}
            />
          </ResizablePanel>

          <ResizableHandle />

          {/* Right Panel - Prompt Editor */}
          <ResizablePanel defaultSize={50} minSize={30}>
            <PromptEditor
              prompt={selectedPrompt}
              taskName={selectedTask?.name}
              onUpdate={handlePromptUpdate}
              onImportComplete={handleImportComplete}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}
